import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  reportCollectionsFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportEconomicAccountingReportSelector,
  reportCollectionsFilterEconomicAccountingGroupBySelector,
  downloadReportPdf,
  reportCollectionsFilterEconomicAccountingSelector,
} from '../../state';
import { PdfReportType } from '../../state/exports/types';
import { EconomicAccountingReport } from '../../types';
import {
  getAccountingInformationReportPdfFilters,
  getRecaPdfParameters,
} from '../AccountingInformationReportDataGrids/getPdfFilters';
import handlerFactory, { EconomicAccountingReportDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<
    EconomicAccountingReport,
    EconomicAccountingReportDataGridContext
  >();

  const { t } = useTranslation();
  const title = t('economic-accounting-report.title');
  const country = useSelector(reportFilterCountryCodeSelector);
  const groupBy = useSelector(reportCollectionsFilterEconomicAccountingGroupBySelector);
  const reportEconomicAccountingReportResource = useSelector(
    reportEconomicAccountingReportSelector
  );
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const { data, loading } = reportEconomicAccountingReportResource;

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
    }),
    [t, country, groupBy]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCollectionsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<
    EconomicAccountingReport,
    EconomicAccountingReportDataGridContext
  >();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const reportCollectionsFilterEconomicAccounting = useSelector(
    reportCollectionsFilterEconomicAccountingSelector
  );

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: ['countryCode', 'stores', 'startBusinessDate', 'endBusinessDate'],
              filters: getAccountingInformationReportPdfFilters(
                reportFilters,
                reportCollectionsFilters
              ),
              pdfReportType: PdfReportType.economicAccounting,
              extraFilters: reportCollectionsFilterEconomicAccounting,
              additionalParameters: getRecaPdfParameters(
                t,
                reportFilters,
                reportCollectionsFilters,
                ['paymentTypes', 'onlyDifferences', 'franchiseType', 'groupBy'],
                reportCollectionsFilterEconomicAccounting.groupBy
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [
    handler,
    dispatch,
    t,
    reportCollectionsFilterEconomicAccounting,
    reportFilters,
    reportCollectionsFilters,
  ]);

  return null;
};

const EconomicAccountingReportDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default EconomicAccountingReportDataGrid;
